import { useEffect, useState } from 'react'
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { useTonConnectUI } from '@tonconnect/ui-react'
import { Plus, Trash2, Shield } from 'lucide-react'
import type { AppContext } from '../components/layout/AppLayout'
import { chatStorage, type ChatSession } from '../lib/chat-storage'
import { STATIC_MODELS } from '../lib/models-static'
import { timeAgo } from '../lib/time-ago'
import { haptics } from '../lib/haptics'
import { getProvider } from '../lib/providers'
import { BottomSheet } from '../components/ui'

function SessionAvatar({ modelId }: { modelId: string }) {
  const { color, label } = getProvider(modelId)
  const isSecure = modelId.startsWith('cocoon/')
  return (
    <div className="relative shrink-0">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-[15px] font-semibold"
        style={{ backgroundColor: color, color: '#ffffff' }}
      >
        {label.charAt(0).toUpperCase()}
      </div>
      {isSecure && (
        <div
          className="absolute -bottom-0.5 -right-0.5 w-[14px] h-[14px] rounded-full flex items-center justify-center"
          style={{
            backgroundColor: '#34c759',
            border: '2px solid var(--tg-theme-bg-color)',
          }}
        >
          <Shield size={8} color="#ffffff" fill="#ffffff" />
        </div>
      )}
    </div>
  )
}

export default function ChatListPage() {
  const { wallet } = useOutletContext<AppContext>()
  const [tonConnectUI] = useTonConnectUI()
  const navigate = useNavigate()
  const location = useLocation()
  const [sessions, setSessions] = useState<ChatSession[]>(() => chatStorage.list())
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // Reload sessions on mount / back-navigation; redirect to new chat if list is empty
  useEffect(() => {
    if (!wallet) return  // Don't auto-create sessions when wallet not connected
    const list = chatStorage.list()
    setSessions(list)

    if (list.length === 0) {
      const defaultModel = STATIC_MODELS[0]
      const session = chatStorage.create(defaultModel.id, defaultModel.name)
      navigate(`/chat/${session.id}`, { replace: true })
    }
  }, [location.key, wallet]) // eslint-disable-line react-hooks/exhaustive-deps

  // No wallet — show connect prompt
  if (!wallet) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-sp-3 px-sp-6">
        <span className="text-[40px] opacity-40">✦</span>
        <p className="text-[15px] text-center" style={{ color: 'var(--tg-theme-hint-color)' }}>
          Connect wallet to start chatting
        </p>
        <button
          onClick={() => { haptics.impact('medium'); tonConnectUI.openModal() }}
          className="h-11 px-sp-6 rounded-[14px] text-[15px] font-semibold active:opacity-70 transition-opacity"
          style={{ backgroundColor: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
        >
          Connect Wallet
        </button>
      </div>
    )
  }

  const handleNew = () => {
    haptics.impact('medium')
    const defaultModel = STATIC_MODELS[0]
    const session = chatStorage.create(defaultModel.id, defaultModel.name)
    navigate(`/chat/${session.id}`)
  }

  const handleDeleteRequest = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    haptics.impact('heavy')
    setDeleteConfirmId(sessionId)
  }

  const handleDeleteConfirm = () => {
    if (!deleteConfirmId) return
    haptics.notification('warning')
    chatStorage.delete(deleteConfirmId)
    const remaining = chatStorage.list()
    if (remaining.length === 0) {
      // Last chat deleted — open a fresh one
      const defaultModel = STATIC_MODELS[0]
      const session = chatStorage.create(defaultModel.id, defaultModel.name)
      navigate(`/chat/${session.id}`, { replace: true })
    } else {
      setSessions(remaining)
    }
    setDeleteConfirmId(null)
  }

  const deleteTarget = deleteConfirmId ? sessions.find((s) => s.id === deleteConfirmId) : null

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header row: "Chats" title + New chat button */}
      <div className="flex items-center justify-between px-sp-4 pt-sp-3 pb-sp-2 shrink-0">
        <span
          className="text-[20px] font-bold"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          Chats
        </span>
        <button
          onClick={handleNew}
          className="flex items-center gap-1.5 h-10 px-sp-3 rounded-full text-[14px] font-semibold active:opacity-70 transition-opacity"
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            color: 'var(--tg-theme-accent-text-color)',
          }}
        >
          <Plus size={16} />
          New chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-area">
        {sessions.map((session) => (
          <div
            key={session.id}
            role="button"
            tabIndex={0}
            onClick={() => {
              haptics.impact('light')
              navigate(`/chat/${session.id}`)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                haptics.impact('light')
                navigate(`/chat/${session.id}`)
              }
            }}
            className="w-full flex items-center gap-sp-3 px-sp-4 text-left active:opacity-70 transition-opacity cursor-pointer"
            style={{
              minHeight: '64px',
              borderBottom: '1px solid var(--tg-theme-secondary-bg-color)',
              paddingTop: '12px',
              paddingBottom: '12px',
            }}
          >
            <SessionAvatar modelId={session.modelId} />

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-sp-2">
                <span
                  className="text-[15px] font-medium truncate"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {session.title.length > 30 ? session.title.slice(0, 30) + '…' : session.title}
                </span>
                <span
                  className="text-[11px] shrink-0 tabular-nums"
                  style={{ color: 'var(--tg-theme-hint-color)' }}
                >
                  {timeAgo(session.updatedAt)}
                </span>
              </div>
              <p
                className="text-[13px] truncate mt-0.5"
                style={{ color: 'var(--tg-theme-hint-color)' }}
              >
                {session.modelName || session.modelId} · {session.messages.length} msg{session.messages.length !== 1 ? 's' : ''}
              </p>
            </div>

            <button
              onClick={(e) => handleDeleteRequest(e, session.id)}
              className="shrink-0 w-10 h-11 flex items-center justify-center active:opacity-70 transition-opacity opacity-40"
              style={{ color: 'var(--tg-theme-destructive-text-color)' }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* Delete confirmation sheet */}
      <BottomSheet
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete chat?"
      >
        <div className="px-sp-4 pb-sp-6 flex flex-col gap-sp-3">
          {deleteTarget && (
            <p className="text-[15px] text-center" style={{ color: 'var(--tg-theme-hint-color)' }}>
              "{deleteTarget.title.length > 40 ? deleteTarget.title.slice(0, 40) + '…' : deleteTarget.title}" will be permanently deleted.
            </p>
          )}
          <button
            onClick={handleDeleteConfirm}
            className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
            style={{ backgroundColor: 'var(--tg-theme-destructive-text-color)', color: '#ffffff' }}
          >
            Delete
          </button>
          <button
            onClick={() => { haptics.impact('light'); setDeleteConfirmId(null) }}
            className="w-full h-14 rounded-[14px] text-[17px] font-semibold active:opacity-70 transition-opacity"
            style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)', color: 'var(--tg-theme-text-color)' }}
          >
            Cancel
          </button>
        </div>
      </BottomSheet>
    </div>
  )
}
